import { useId, useState, type FormEvent } from 'react';
import { GitBranch } from 'lucide-react';
import {
  BRANCH_STATUSES,
  BRANCH_TYPES,
  type Branch,
  type BranchStatus,
  type BranchType,
  type CloudPosStatus,
  type Pharmacy,
} from '@quds/shared';
import { LocationFields } from '@/components/location';
import { Button, FormField, Input, Modal, Select, fieldDescribedBy, useToast } from '@/components/ui';
import { createBranch, createProductionBranch, updateBranch, updateProductionBranch } from '@/data';
import { ApiError, errorMessage } from '@/lib/api';
import { BRANCH_TYPE_LABELS } from '../components/labels';
import { hasErrors, mapFieldIssues, type FieldErrors } from '../validation';

interface BranchValues {
  name: string;
  referenceCode: string;
  type: BranchType;
  country: string;
  administrativeAreaLevel1: string;
  city: string;
  area: string;
  address: string;
  status: BranchStatus;
}

const BRANCH_ID = /^[A-Z0-9][A-Z0-9-]{1,15}$/;

function validate(v: BranchValues): FieldErrors<BranchValues> {
  const e: FieldErrors<BranchValues> = {};
  if (!v.name.trim()) e.name = 'Enter the branch name.';
  if (!BRANCH_ID.test(v.referenceCode.trim())) e.referenceCode = 'Use 2–16 letters, numbers or dashes, e.g. BR-01.';
  if (!v.city.trim()) e.city = 'Enter the city.';
  return e;
}

export type BranchFormContext = {
  branch: Branch | null;
  /** Linked Cloud branch UUID when Cloud POS is connected. */
  productionBranchId?: string | null;
};

/** Add or edit a Branch. Branch ID maps to CA referenceCode and Cloud branch_ref. */
export function BranchFormModal({
  pharmacy,
  context,
  cloudPosStatus = 'not_connected',
  canSyncCloudBranchId = true,
  onClose,
}: {
  pharmacy: Pharmacy;
  context: BranchFormContext;
  cloudPosStatus?: CloudPosStatus;
  /** Requires tenant.manage — sync Branch ID to Cloud branch_ref. */
  canSyncCloudBranchId?: boolean;
  onClose: () => void;
}) {
  const { branch, productionBranchId = null } = context;
  const { notify } = useToast();
  const base = useId();
  const id = (f: keyof BranchValues) => `${base}-${f}`;
  const [values, setValues] = useState<BranchValues>(() => ({
    name: branch?.name ?? '',
    referenceCode: branch?.referenceCode ?? '',
    type: branch?.type ?? 'secondary',
    country: branch?.country ?? pharmacy.country,
    administrativeAreaLevel1: branch?.administrativeAreaLevel1 ?? pharmacy.administrativeAreaLevel1 ?? '',
    city: branch?.city ?? pharmacy.city,
    area: branch?.area ?? pharmacy.area ?? '',
    address: branch?.address ?? '',
    status: branch?.status ?? 'active',
  }));
  const [errors, setErrors] = useState<FieldErrors<BranchValues>>({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const patch = (p: Partial<BranchValues>) => {
    const next = { ...values, ...p };
    setValues(next);
    if (hasErrors(errors)) setErrors(validate(next));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const found = validate(values);
    setErrors(found);
    if (hasErrors(found)) return;
    setSaving(true);
    setSubmitError(null);
    const branchId = values.referenceCode.trim().toUpperCase();
    const input = {
      name: values.name.trim(),
      referenceCode: branchId,
      type: values.type,
      country: values.country,
      administrativeAreaLevel1: values.administrativeAreaLevel1.trim() || null,
      city: values.city.trim(),
      area: values.area.trim() || null,
      address: values.address.trim() || null,
      status: values.status,
    };
    try {
      if (branch) {
        await updateBranch(branch.id, input);
        if (canSyncCloudBranchId && productionBranchId) {
          await updateProductionBranch(pharmacy.id, productionBranchId, {
            name: input.name,
            branchRef: branchId,
            isActive: input.status === 'active',
            idempotencyKey: crypto.randomUUID(),
          });
        }
        notify({ tone: 'success', title: 'Branch updated', description: 'Saved and recorded in the activity log.' });
      } else {
        const created = await createBranch(pharmacy.id, input);
        if (cloudPosStatus === 'connected' || cloudPosStatus === 'partially_connected') {
          await createProductionBranch(pharmacy.id, {
            branch: {
              branchRef: created.referenceCode,
              name: created.name,
              localBranchId: created.id,
              primary: created.type === 'main',
            },
            createApiKeyForFirstBranch: false,
          });
          notify({
            tone: 'success',
            title: 'Branch added',
            description: 'Created under the same Cloud POS account. Generate API from the Branches tab.',
          });
        } else {
          notify({
            tone: 'success',
            title: 'Branch added',
            description: 'Cloud POS is not connected yet — use Set Up Cloud POS before generating an API.',
          });
        }
      }
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.fieldIssues.length) setErrors(mapFieldIssues<BranchValues>(err.fieldIssues, {}));
      setSubmitError(errorMessage(err, 'Could not save the branch.'));
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={branch ? 'Edit Branch' : 'Add Branch'}
      description={
        branch
          ? pharmacy.name
          : cloudPosStatus === 'not_connected'
            ? `${pharmacy.name} · Cloud POS Not Connected`
            : `${pharmacy.name} · will connect under the same Cloud POS account`
      }
      icon={<GitBranch size={18} aria-hidden="true" />}
      dismissible={!saving}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form="branch-form" loading={saving}>
            {branch ? 'Save Changes' : 'Add Branch'}
          </Button>
        </>
      }
    >
      {submitError && (
        <p role="alert" className="mb-4 rounded-md border border-danger/20 bg-danger-soft px-3 py-2 text-[13px] text-danger">
          {submitError}
        </p>
      )}
      <form id="branch-form" noValidate onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-2">
        <FormField label="Branch Name" htmlFor={id('name')} required error={errors.name} className="sm:col-span-2">
          <Input id={id('name')} value={values.name} onChange={(e) => patch({ name: e.target.value })} maxLength={120} invalid={!!errors.name} aria-describedby={fieldDescribedBy(id('name'), { error: errors.name })} autoFocus />
        </FormField>
        <FormField
          label="Branch ID"
          htmlFor={id('referenceCode')}
          required
          error={errors.referenceCode}
          hint="Must match the Branch ID configured in the pharmacy POS."
        >
          <Input
            id={id('referenceCode')}
            value={values.referenceCode}
            onChange={(e) => patch({ referenceCode: e.target.value.toUpperCase() })}
            placeholder="BR-01"
            maxLength={16}
            className="font-mono uppercase"
            invalid={!!errors.referenceCode}
            aria-describedby={fieldDescribedBy(id('referenceCode'), { hint: true, error: errors.referenceCode })}
          />
        </FormField>
        <FormField label="Branch Type" htmlFor={id('type')} error={errors.type}>
          <Select id={id('type')} value={values.type} onChange={(e) => patch({ type: e.target.value as BranchType })} options={BRANCH_TYPES.map((t) => ({ value: t, label: BRANCH_TYPE_LABELS[t] }))} invalid={!!errors.type} />
        </FormField>

        <LocationFields
          values={{
            country: values.country,
            administrativeAreaLevel1: values.administrativeAreaLevel1,
            city: values.city,
            area: values.area,
            address: values.address,
          }}
          errors={{
            country: errors.country,
            administrativeAreaLevel1: errors.administrativeAreaLevel1,
            city: errors.city,
            area: errors.area,
            address: errors.address,
          }}
          onChange={patch}
        />

        {branch && (
          <FormField label="Status" htmlFor={id('status')}>
            <Select id={id('status')} value={values.status} onChange={(e) => patch({ status: e.target.value as BranchStatus })} options={BRANCH_STATUSES.map((s) => ({ value: s, label: s === 'active' ? 'Active' : 'Inactive' }))} />
          </FormField>
        )}
      </form>
    </Modal>
  );
}
